import type {TFunction, TOptions} from 'i18next';
import type {
  FieldErrors,
  FieldValues,
  Path,
  UseFormSetError,
} from 'react-hook-form';
import {z} from 'zod';
import type {
  FormDataKey,
  FormDataValue,
  FormSubmission,
  GetSchemaShape,
} from '@/app/forms/types';
import {ApiError} from '@/app/forms/types';
import * as constants from './constants';

export function objectToFormData<T extends FormSubmission>(obj: T): FormData {
  const formData = new FormData();

  for (const key of Object.keys(obj) as (keyof T)[]) {
    const value = obj[key];

    if (value == null) {
      continue;
    }

    if (value instanceof FileList || Array.isArray(value)) {
      for (const val of value) {
        formData.append(String(key), val);
      }
    } else {
      formData.append(String(key), value);
    }
  }

  return formData;
}

function isArraySchemaType(schema: unknown): boolean {
  let current: unknown = schema;
  while (current && typeof current === 'object') {
    if (current instanceof z.ZodArray) {
      return true;
    }
    const curr = current as {
      type?: string;
      element?: unknown;
      _def?: {
        typeName?: string;
        type?: string;
        schema?: unknown;
        innerType?: unknown;
        element?: unknown;
      };
    };
    if (
      curr.type === 'array' ||
      curr._def?.type === 'array' ||
      curr._def?.typeName === 'ZodArray' ||
      'element' in curr ||
      (curr._def && 'element' in curr._def)
    ) {
      return true;
    }
    if (curr._def?.schema) {
      current = curr._def.schema;
    } else if (curr._def?.innerType) {
      current = curr._def.innerType;
    } else if (curr._def?.type && typeof curr._def.type === 'object') {
      current = curr._def.type;
    } else {
      break;
    }
  }
  return false;
}

export function formDataToObject(
  formData: FormData,
  schema?: z.ZodTypeAny,
): FormSubmission {
  const obj: FormSubmission = {};
  const shape = schema
    ? (getSchemaShape(schema) as Record<string, unknown>)
    : undefined;

  for (const key of new Set(formData.keys())) {
    const values = formData.getAll(key);
    const fieldSchema = shape?.[key];

    if (fieldSchema && isArraySchemaType(fieldSchema)) {
      obj[key] = values;
    } else {
      obj[key] = values.length > 1 ? values : values[0];
    }
  }

  return obj;
}

export function splitFormData(
  formData: FormData,
  condition: (key: FormDataKey, value: FormDataValue) => boolean,
): [FormData, FormData] {
  const fd1 = new FormData();
  const fd2 = new FormData();

  for (const [key, value] of formData.entries()) {
    if (condition(key, value)) {
      fd1.append(key, value);
    } else {
      fd2.append(key, value);
    }
  }
  return [fd1, fd2];
}

export async function fileAsVersionData(file: File) {
  return Buffer.from(await file.arrayBuffer()).toString('base64');
}

export function buildSafeFilename(
  linkedEntityId: string,
  filename: string,
): string {
  const ext = getFileExtension(filename);
  const timestamp = Date.now();
  const randomString = randomSuffix();

  const base = `${linkedEntityId}_${timestamp}_${randomString}`;
  return ext ? `${base}.${ext}` : base;
}

export function getFileExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf('.');
  // No extension if dot is not found, or if it's the first character
  // (e.g. ".env")
  if (lastDotIndex < 1) {
    return '';
  }
  return filename.substring(lastDotIndex + 1).toLowerCase();
}

function randomSuffix(): string {
  const buf = crypto.getRandomValues(new Uint32Array(1));
  return buf[0].toString(16).padStart(8, '0');
}

/**
 * Formats a raw byte number into a human-readable string.
 * * @param bytes - The size of the file in bytes.
 * @param decimals - The number of decimal places to display. Defaults to 1.
 */
export const formatBytes = (bytes: number, decimals = 1): string => {
  if (bytes === 0) return '0 Bytes';

  const unitIndex = Math.floor(
    Math.log(bytes) / Math.log(constants.KILO_BYTE_SIZE),
  );

  const sizeInUnit = bytes / constants.KILO_BYTE_SIZE ** unitIndex;

  return `${Number.parseFloat(sizeInUnit.toFixed(decimals))} ${constants.FILE_SIZE_UNITS[unitIndex]}`;
};

/**
 * Shared utility to handle form errors from API responses.
 * Automatically wraps generic errors into a standard ApiError format.
 * @template T - The form data type
 * @param error - The error object (ApiError or generic Error)
 * @param setError - React Hook Form's setError function
 * @param fields - The shape of the Zod schema (to validate field names)
 */
export function handleError<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields: Record<keyof T, unknown>,
): void {
  let apiError: ApiError;

  if (error instanceof ApiError) {
    apiError = error;
  } else {
    apiError = new ApiError(
      'Failed to save form data. Please try again later.',
      {
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
      },
      {cause: error},
    );
  }

  if (apiError.data.code === 'VALIDATION_ERROR') {
    const {formErrors, fieldErrors} = apiError.data.errors || {};

    if (fieldErrors) {
      for (const [name, errors] of Object.entries(fieldErrors)) {
        if (Object.hasOwn(fields, name)) {
          setError(name as Path<T>, {
            type: 'manual',
            message: errors.join(', '),
          });
        }
      }
    }

    if (formErrors && formErrors.length > 0) {
      setError('root', {
        type: 'manual',
        message: formErrors.filter(Boolean).join(', ') || apiError.message,
      });
    }
  } else {
    setError('root', {
      type: 'manual',
      message: apiError.message,
    });
  }
}

/**
 * Handles failed form submissions in React Hook Form.
 * Executes when validation rules aren't met, blocking 'onSubmit'
 * and logging the specific field errors for debugging.
 * @param errors Object containing details for all failed field validations.
 */
export const onSubmitError = <T extends FieldValues>(
  errors: FieldErrors<T>,
) => {
  console.error('BLOCKING ERRORS:', errors);
};

/**
 * Performs i18n translation if a non-empty string is provided.
 * @param message The string (key) to translate, or undefined.
 * @param t The translation function.
 * @param tOptions The optional translation options and variables.
 * @return The translated string, or undefined if no message is provided.
 */
export const tSafe = (
  message: string | undefined,
  t: TFunction,
  tOptions?: TOptions,
) => {
  return message ? t(message, tOptions) : undefined;
};

/**
 * Attempts to get the form-level (i.e. "root") error message, either from an
 * explicitly set root error, or a general message, in the case of a submitted
 * form with invalid fields.
 * @param errors The form errors object.
 * @param isSubmitted Whether or not the form is in the submitted state.
 * @param isValid Whether or not the form data is in a valid state.
 * @param t The translation function for the general error message.
 * @return The root error message, or undefined if no errors.
 */
export function getRootError(
  errors: FieldErrors,
  isSubmitted: boolean,
  isValid: boolean,
  t: TFunction,
): string | undefined {
  if (errors.root?.message) {
    return t(errors.root.message);
  }
  if (isSubmitted && !isValid) {
    return t('errors.general');
  }
  return undefined;
}

/**
 * Returns a date string in YYYY-MM-DD format relative to the current local date.
 * @param {number} [offset=0] - The number of days to offset from today.
 * @returns {string} A formatted date string (YYYY-MM-DD).
 */
export const getRelativeDateString = (offset = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return formatISODate(date);
};

/**
 * Formats a Date object or a date string into a YYYY-MM-DD string using
 * the local timezone and the en-CA locale to ensure ISO-like formatting.
 * @param {Date | string} date - The date object or string to be formatted.
 * @returns {string} A formatted date string (YYYY-MM-DD).
 */
export const formatISODate = (date: Date | string) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-CA', {
    dateStyle: 'short',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(d);
};

export const getTodayDateString = () => getRelativeDateString(0);

export const getSfdcLanguage = (locale?: string | string[]): string => {
  const lang = Array.isArray(locale) ? locale[0] : locale;
  return lang?.startsWith('es') ? 'Spanish' : 'English';
};

export const getSchemaShape = <T extends z.ZodTypeAny>(
  schema: T,
): GetSchemaShape<T> => {
  let current: unknown = schema;
  while (
    current &&
    typeof current === 'object' &&
    !('shape' in current) &&
    '_def' in current
  ) {
    const def = (current as {_def?: {schema?: unknown; innerType?: unknown}})
      ._def;
    if (def?.schema) {
      current = def.schema;
    } else if (def?.innerType) {
      current = def.innerType;
    } else {
      break;
    }
  }
  if (current && typeof current === 'object' && 'shape' in current) {
    return (current as {shape: GetSchemaShape<T>}).shape;
  }
  return {} as GetSchemaShape<T>;
};

/**
 * Extracts field key names from a Zod object schema, unwrapping refinements if needed.
 */
export const getSchemaKeys = (schema: z.ZodTypeAny): string[] =>
  Object.keys(getSchemaShape(schema));
